/**
 * useSync Hook
 * React Query hook for collection pipeline actions
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { SyncResponse } from "../types/admin";

export type CollectionAction = "discover" | "download" | "convert" | "extract" | "index";

/**
 * Trigger a manual collection action
 */
async function triggerCollectionAction(params: {
  collectionId: number;
  action: CollectionAction;
}): Promise<SyncResponse> {
  const response = await fetch(`/api/collection/${params.collectionId}/${params.action}`, {
    method: "POST",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? "Fout bij uitvoeren van collectie-actie");
  }

  return response.json();
}

/**
 * Hook to trigger a collection action
 */
export function useTriggerCollectionAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerCollectionAction,
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["collections"] });
      void queryClient.invalidateQueries({
        queryKey: ["collectionDocuments", variables.collectionId],
      });
      void queryClient.invalidateQueries({ queryKey: ["collection-jobs", variables.collectionId] });
    },
  });
}
